import React from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import type { Placement } from "react-bootstrap/esm/types";

// The hover bubble the admin app renders through. It started on the leads
// surfaces - the building-match, geocoder, confidence and tenant-commercial
// hovers - and sat in `routes/leads/`, which is why the other three raw
// `OverlayTrigger` call sites in this app (Costs, DocumentNumbersCard,
// SiteHealthStatus) each rebuilt the same wrapper with slightly different
// tooltip styling. It lives here so they can share it.
//
// Imported by its own path rather than through `components/index.ts`: that
// barrel also pulls in the date pickers, whose stylesheet import jest cannot
// parse, so a component test that renders anything tooltip-bearing fails to
// load at all.
//
// Absent text renders the child alone rather than an empty bubble: most call
// sites pass a value that is only sometimes there, and a wrapper that has to be
// conditionally applied is one every caller gets to forget.
export const WithTooltip: React.FC<{
  text: React.ReactNode;
  children: React.ReactElement;
  placement?: Placement;
  // Widen the tooltip past Bootstrap's 200px default. A long body (e.g. the
  // agent's reasoning on the confidence hover) otherwise wraps into a tall,
  // narrow column that clips at the viewport edge; a wider box is shorter and
  // stays on screen. Drives `--bs-tooltip-max-width`, which `.tooltip-inner`
  // reads (Bootstrap 5.3).
  maxWidth?: number;
  className?: string;
}> = ({ text, children, placement = "top", maxWidth, className }) => {
  if (!text) return children;
  return (
    <OverlayTrigger
      placement={placement}
      overlay={
        <Tooltip
          className={className}
          style={
            {
              whiteSpace: "pre-line",
              textAlign: "left",
              ...(maxWidth
                ? { "--bs-tooltip-max-width": `${maxWidth}px` }
                : {}),
            } as React.CSSProperties
          }
        >
          {text}
        </Tooltip>
      }
    >
      {children}
    </OverlayTrigger>
  );
};

export default WithTooltip;
